import { tmdbRequest, filterReleased, filterIndian, normalizeTVItem } from './tmdb';

// ---------------------------------------------------------------------------
// Infinite-scroll feed over TMDB /discover.
//
// Every catalog page (Home, Movies, Series) is one long newest-first stream of
// Indian titles instead of a fixed set of lanes. /discover is used (not /movie/popular)
// because it is the only TMDB list endpoint that accepts sort_by, with_genres,
// vote_average.gte and a release-date ceiling together — which is what lets the
// infinite scroll stay correctly ordered and pre-filtered instead of sorting
// client-side over an arbitrary fixed slice.
// ---------------------------------------------------------------------------

const FEED_KINDS = {
  movie: { path: '/discover/movie', dateParam: 'primary_release_date.lte' },
  tv: { path: '/discover/tv', dateParam: 'first_air_date.lte' },
};

// UI sort ids -> TMDB sort_by values. `newest` is the default everywhere so the
// newest Indian title is always at the top of the feed.
const SORT_BY = {
  movie: {
    newest: 'primary_release_date.desc',
    popular: 'popularity.desc',
    'rating-desc': 'vote_average.desc',
    'title-asc': 'original_title.asc',
  },
  tv: {
    newest: 'first_air_date.desc',
    popular: 'popularity.desc',
    'rating-desc': 'vote_average.desc',
    'title-asc': 'original_title.asc',
  },
};

const todayStr = () => new Date().toISOString().slice(0, 10);

// TMDB serves up to 20 results per discover page and caps deep pagination.
// Clamping here keeps the cursor honest instead of asking for pages that 400.
const MAX_PAGE = 500;

// Per-page share of each language lane.
//
// Har /discover stream 20 titles deta hai. 7 languages ko bina cap ke merge
// karte to Gujarati + South ka total pool hi merge ko dominate kar deta —
// user ko "sirf Gujarati" dikhta. Quotas ke saath har lane page me apni
// limited jagah leti hai: Bollywood sabse upar, phir South, Hollywood,
// aur Gujarati sabse kam (user ne kaha ki Gujarati jyada aa rahi hai).
const FEED_LANGUAGE_QUOTAS = [
  { lang: 'hi', cap: 10 },   // Bollywood
  { lang: 'ta', cap: 2 },    // South — Tamil
  { lang: 'te', cap: 2 },    // South — Telugu
  { lang: 'kn', cap: 2 },    // South — Kannada
  { lang: 'ml', cap: 1 },    // South — Malayalam
  { lang: 'en', cap: 5 },    // Hollywood (Hindi-dubbed playback)
  { lang: 'gu', cap: 3 },    // Gujarati — deliberately the smallest lane
];

const FEED_LANGUAGES = FEED_LANGUAGE_QUOTAS.map((entry) => entry.lang);

const quotaFor = (lang) => {
  const found = FEED_LANGUAGE_QUOTAS.find((entry) => entry.lang === lang);
  return found ? found.cap : 0;
};

// Sorting purely by vote_average surfaces unrated placeholder titles at the top,
// so rating sorts always require a minimum vote count.
const RATING_SORT_MIN_VOTES = 50;

export const MOVIE_SORT_OPTIONS = [
  { id: 'newest', name: 'Newest Releases' },
  { id: 'popular', name: 'Most Popular' },
  { id: 'rating-desc', name: 'Highest Rated' },
  { id: 'title-asc', name: 'Title (A to Z)' },
];

export const TV_SORT_OPTIONS = [
  { id: 'newest', name: 'Airing / Recent' },
  { id: 'popular', name: 'Most Popular' },
  { id: 'rating-desc', name: 'Highest Rated' },
  { id: 'title-asc', name: 'Title (A to Z)' },
];

const buildQuery = ({ kind, sort, genreId, minRating }) => {
  const config = FEED_KINDS[kind];
  if (!config) throw new Error(`Unsupported feed kind: ${kind}`);

  const sortBy = SORT_BY[kind][sort] || SORT_BY[kind].newest;

  const params = {
    sort_by: sortBy,
    page: 1,
    // Released-only ceiling — mirrors filterReleased used by the fixed lanes.
    [config.dateParam]: todayStr(),
  };

  if (genreId && genreId !== 'all' && Number(genreId) > 0) {
    params.with_genres = String(genreId);
  }

  if (Number(minRating) > 0) {
    params['vote_average.gte'] = String(Number(minRating));
  } else if (sortBy === 'vote_average.desc') {
    params['vote_count.gte'] = String(RATING_SORT_MIN_VOTES);
  }

  return params;
};

const dedupe = (items) =>
  items.filter((item, index, self) => index === self.findIndex((other) => other.id === item.id));

// Merging two independently sorted streams leaves the page grouped by language, so the
// page is re-sorted with the same key TMDB sorted each stream by.
const buildComparator = (sort) => {
  if (sort === 'title-asc') {
    return (a, b) =>
      String(a.original_title || a.title || '').localeCompare(
        String(b.original_title || b.title || ''),
        'en'
      );
  }

  if (sort === 'newest') {
    // Both kinds are normalized onto `release_date` before this runs.
    return (a, b) => String(b.release_date || '').localeCompare(String(a.release_date || ''));
  }

  if (sort === 'rating-desc') {
    return (a, b) => (Number(b.vote_average) || 0) - (Number(a.vote_average) || 0);
  }

  return (a, b) => (Number(b.popularity) || 0) - (Number(a.popularity) || 0);
};

/**
 * Fetch a single page of the infinite feed.
 *
 * Unlike `tmdbService`, failures reject: the feed renders an explicit error state
 * with a Retry affordance, so a silent empty array would be indistinguishable
 * from "no Indian titles matched".
 *
 * @returns {Promise<{items: Array, page: number, totalPages: number, totalResults: number}>}
 */
export const fetchFeedPage = async ({
  kind,
  page = 1,
  sort = 'newest',
  genreId = 'all',
  minRating = 0,
} = {}) => {
  const config = FEED_KINDS[kind];
  if (!config) throw new Error(`Unsupported feed kind: ${kind}`);

  const targetPage = Math.min(Math.max(1, Number(page) || 1), MAX_PAGE);
  const params = buildQuery({ kind, sort, genreId, minRating });

  // /discover takes exactly one value in with_original_language — "hi,gu" returns
  // total_results 0. /movie/popular & co. do accept the comma list, which is why
  // the fixed catalog lanes never hit this. So the feed fans out per language and
// merges the streams back into one correctly-ordered page.
  const settled = await Promise.allSettled(
    FEED_LANGUAGES.map((lang) =>
      tmdbRequest(config.path, {
        ...params,
        page: targetPage,
        with_original_language: lang,
        region: 'IN',
      })
    )
  );

  const fulfilled = settled.filter((result) => result.status === 'fulfilled');
  if (fulfilled.length === 0) throw settled[0].reason;

  const streams = fulfilled.map((result) => result.value?.data ?? {});
  const rawItems = streams.flatMap((data) => (Array.isArray(data?.results) ? data.results : []));

  let items = filterReleased(filterIndian(rawItems));
  if (kind === 'tv') items = items.map(normalizeTVItem);

  // Cap each language lane BEFORE the global dedupe so no single language can
  // swamp the merged page. Sorting first means the cap keeps that lane's best
  // titles, and slicing the *unfiltered* stream count tracks the TMDB cursor.
  const perLane = new Map();
  items.forEach((item) => {
    const lang = item?.original_language;
    if (!lang) return;
    const bucket = perLane.get(lang) || [];
    bucket.push(item);
    perLane.set(lang, bucket);
  });

  const capped = [];
  perLane.forEach((bucket, lang) => {
    const cap = quotaFor(lang);
    if (cap <= 0) return;
    capped.push(...bucket.slice(0, cap));
  });

  items = dedupe(capped).sort(buildComparator(sort));

  const totalPages = Math.max(...streams.map((data) => Number(data?.total_pages) || 1), targetPage);
  const totalResults = streams.reduce((sum, data) => sum + (Number(data?.total_results) || 0), 0);

  // Keep scrolling while any language stream still has pages, so the short
  // lanes are exhausted before the feed reports it is done. An empty page ends
  // the feed regardless of what total_pages claims, or it would request forever.
  const hasMore =
    rawItems.length > 0 &&
    targetPage < MAX_PAGE &&
    streams.some((data) => targetPage < (Number(data?.total_pages) || 1));

  return {
    items,
    page: targetPage,
    totalPages: Math.min(totalPages, MAX_PAGE),
    totalResults: totalResults || items.length,
    hasMore,
  };
};

export const movieFeed = {
  fetch: (options) => fetchFeedPage({ ...options, kind: 'movie' }),
};

export const tvFeed = {
  fetch: (options) => fetchFeedPage({ ...options, kind: 'tv' }),
};

const tmdbFeed = { fetchFeedPage, movieFeed, tvFeed };

export default tmdbFeed;
